import { EditorNode } from '../core/EditorNode';
import { normalizeColorToHex, getColorLuminance } from '../utils/material-utils';

export class ColorNode extends EditorNode {
  constructor() {
    super('color');
  }

  render(data, context) {
    const { makeDraggable, onClick } = context;
    const { id, value, displayValue } = data;

    const normalizedColor = normalizeColorToHex(value);
    const luminance = getColorLuminance(normalizedColor);
    const isVeryLight = luminance > 200;
    const isLight = luminance > 160;

    const container = document.createElement('span');
    container.contentEditable = 'false';
    container.setAttribute('data-type', 'color');
    container.setAttribute('data-color-id', id);
    container.setAttribute('data-value', value);
    container.setAttribute('data-display-value', displayValue || normalizedColor);

    container.className =
      'color-tag inline-flex items-center gap-1.5 mx-0.5 px-1 py-0.5 rounded-full select-none align-baseline transition-all duration-200';

    const textColor = isVeryLight ? '#666666' : isLight ? '#444444' : normalizedColor;
    const bgOpacity = isVeryLight ? 0.4 : isLight ? 0.2 : 0.15;
    const borderOpacity = isVeryLight ? '60' : isLight ? '40' : '30';

    container.style.cssText = `
      background-color: ${normalizedColor}${Math.round(bgOpacity * 255)
        .toString(16)
        .padStart(2, '0')};
      color: ${textColor};
      font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, Monaco, Consolas, monospace;
      font-size: 0.8125em;
      font-weight: 500;
      letter-spacing: -0.01em;
      line-height: 1.4;
      border: 1px solid ${normalizedColor}${borderOpacity};
      transition: all 0.2s ease;
      cursor: pointer;
    `;

    if (makeDraggable) {
      makeDraggable(container);
    }

    if (onClick) {
      container.addEventListener('click', (e) => {
        if (container.classList.contains('dragging')) return;
        e.preventDefault();
        e.stopPropagation();
        onClick(
          {
            id: id,
            value: value,
            displayValue: displayValue || normalizedColor,
          },
          container,
          e
        );
      });
    }

    const colorSwatch = document.createElement('span');
    colorSwatch.className = 'flex-shrink-0';
    const swatchShadow = isVeryLight
      ? 'inset 0 0 0 1px rgba(0,0,0,0.2), 0 0 0 1px rgba(0,0,0,0.08)'
      : 'inset 0 0 0 1px rgba(0,0,0,0.1)';
    colorSwatch.style.cssText = `
      width: 12px;
      height: 12px;
      border-radius: 50%;
      background-color: ${normalizedColor};
      box-shadow: ${swatchShadow};
    `;

    const valueText = document.createElement('span');
    valueText.className = 'pointer-events-none';
    valueText.textContent = normalizedColor;

    container.appendChild(colorSwatch);
    container.appendChild(valueText);

    return container;
  }

  update(element, data, _context) {
    const { value } = data;
    const normalized = normalizeColorToHex(value);
    const luminance = getColorLuminance(normalized);
    const isVeryLight = luminance > 200;
    const isLight = luminance > 160;

    const textColor = isVeryLight ? '#666666' : isLight ? '#444444' : normalized;
    const bgOpacity = isVeryLight ? 0.4 : isLight ? 0.2 : 0.15;
    const borderOpacity = isVeryLight ? '60' : isLight ? '40' : '30';

    element.setAttribute('data-value', value);
    element.setAttribute('data-display-value', normalized);
    element.style.backgroundColor = `${normalized}${Math.round(bgOpacity * 255)
      .toString(16)
      .padStart(2, '0')}`;
    element.style.color = textColor;
    element.style.borderColor = `${normalized}${borderOpacity}`;

    const swatch = element.querySelector('span:first-child');
    if (swatch) {
      swatch.style.backgroundColor = normalized;
      const swatchShadow = isVeryLight
        ? 'inset 0 0 0 1px rgba(0,0,0,0.2), 0 0 0 1px rgba(0,0,0,0.08)'
        : 'inset 0 0 0 1px rgba(0,0,0,0.1)';
      swatch.style.boxShadow = swatchShadow;
    }

    const text = element.querySelector('span:last-child');
    if (text) {
      text.textContent = normalized;
    }
  }

  serialize(element) {
    const value = element.getAttribute('data-value');
    return value; // Color tags in original code seemed to just be the color string or specific format.
  }
}
